import React, {useEffect, useState} from "react";
import {Skeleton, Box, Paper, Stack, useTheme, alpha, Pagination} from "@mui/material";
import {useNavigate, useLocation} from "react-router-dom";
import CustomButton from "../../../core/components/ui/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useVehicle from "../hooks/useVehicle";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import CustomChipComponent from "../../../core/components/common/custom-chip.component";
import ListVehicleAccessLogComponent from "./access-logs/list-vehicle-access-logs.component";
import CustomTableLoaderComponent from "../../../core/components/common/custom-table-loader.component";


const headers = ["Plate Number", "Entry Type", "Purpose", "Timestamp"];

const ViewVehicleComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const {isViewingVehicle, vehicleDetails, getViewVehicle, isFetchingAccessLogs, vehicleAccessLogs, getVehicleAccessLogs} = useVehicle();

    const [vehicleAccessLogsData, setVehicleAccessLogsData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const [vehicleDetailsData, setVehicleDetailsData] = useState(null);

    const rowsPerPage = 10;
    const vehicleId = new URLSearchParams(location.search).get("id");
    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");
  

    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee; 

        if (organizationId) {
            const parameters = {
                vehicleUID: vehicleId,
                orgUID: organizationId
            };
        
            getViewVehicle(parameters);

        } else {
            const parameters = {
                vehicleUID: vehicleId, 
            };
        
            getViewVehicle(parameters);
        }   
    }, [vehicleId, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);

    useEffect(() => {
        if (vehicleDetails) {
            setVehicleDetailsData(vehicleDetails);
        }
    }, [vehicleDetails])

    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId && vehicleDetails && vehicleDetails?.plate_number) {
            const parameters = {
                plateNumber: vehicleDetails?.plate_number,   
                page: currentPage,
                limit: rowsPerPage,
                orgUID: organizationId
            };

            getVehicleAccessLogs(parameters);

        } else if (!organizationId && vehicleDetails && vehicleDetails?.plate_number) {
            const parameters = {
                plateNumber: vehicleDetails?.plate_number,
                page: currentPage,
                limit: rowsPerPage,
            };
        
            getVehicleAccessLogs(parameters);
        }
    }, [vehicleDetails, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);

    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId && vehicleDetails && currentPage && vehicleDetails?.plate_number) {
             const parameters = {
                plateNumber: vehicleDetails?.plate_number,
                page: currentPage,
                limit: rowsPerPage,
                orgUID: organizationId
            }

            getVehicleAccessLogs(parameters);

        } else if (!organizationId && vehicleDetails && currentPage && vehicleDetails?.plate_number) {
            const parameters = {
                plateNumber: vehicleDetails?.plate_number,
                page: currentPage,
                limit: rowsPerPage,
            }
          
            getVehicleAccessLogs(parameters);
        }
    }, [vehicleDetails, currentPage, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee])

    useEffect(() => {
        if (vehicleAccessLogs) {
            setVehicleAccessLogsData(vehicleAccessLogs?.content);
            setTotalPages(vehicleAccessLogs?.totalPages);    
        }
    }, [vehicleAccessLogs])


    const handleChangePagination = (event, page) => { 
        setCurrentPage(page); 
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/vehicles");
        }
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Vehicle List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{textAlign: "start", mt: 2}}>
                <CustomTypography 
                    variant="h3"
                    text={"Vehicle Details"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"View and manage vehicle information"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack spacing={0} flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Vehicle Reference"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(isViewingVehicle) ?
                            <Skeleton variant="text" height="35px" width="34%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        : 
                            <CustomTypography
                                variant="body1"
                                text={(vehicleDetailsData?.vehicle_uid)}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 600,
                                }}
                            />
                        }
                    </Stack>
                </Stack>

                <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1} direction={"column"}>
                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Plate Number"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingVehicle) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(vehicleDetailsData?.plate_number)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Model & Manufacturer"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingVehicle) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(vehicleDetailsData?.model_manufacturer)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                } 
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Type"}
                                    customStyles={{
                                        marginBottom: 0, 
                                    }} 
                                />

                                {(isViewingVehicle) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={vehicleDetailsData?.type}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"OR/CR"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingVehicle) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={vehicleDetailsData?.or_cr}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />    
                                }   
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Status"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingVehicle) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomChipComponent category="vehicleStatus"
                                        label={(vehicleDetailsData?.status === "Active") ? "Active" 
                                            : 
                                                (vehicleDetailsData?.status === "Deactivated") ? "Deactivated"
                                            :
                                                (vehicleDetailsData?.status === "Suspended") ? "Suspended"
                                            :
                                                (vehicleDetailsData?.status === "Removed") ? "Removed"
                                            :
                                                vehicleDetailsData?.status
                                        }
                                    />
                                }
                            </Box>
                            
                            {(vehicleDetailsData?.status !== "Active") && 
                                <Box>
                                    <CustomTypography 
                                        variant="caption" 
                                        text={"Reason"} 
                                        customStyles={{
                                            marginBottom: 0, 
                                        }}
                                    />

                                    {(isViewingVehicle) ?
                                        <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                    :
                                        <CustomTypography
                                            variant="body1"
                                            text={(vehicleDetailsData?.purpose || "N / A")}
                                            customStyles={{
                                                marginBottom: 0, 
                                                fontWeight: 500,
                                            }}
                                        />
                                    }
                                </Box>
                            }
                        </Stack>
                </Stack>           
            </Paper>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flex={1}>
                        <Box sx={{ textAlign: "start" }}>
                            <CustomTypography 
                                variant="h3"
                                text={"Access Logs"}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontSize: 20,
                                    fontWeight: 700
                                }}
                            />

                            <CustomTypography 
                                variant="caption"
                                text={"Monitor vehicle access logs"}
                                customStyles={{
                                    marginBottom: 0,
                                    fontSize: 14 
                                }}
                            />
                        </Box>
                    </Stack>
            
                    <Box sx={{mt: "8px !important"}}>
                        {(isFetchingAccessLogs) ?
                            <Box sx={{mt: 3}}>
                                <CustomTableLoaderComponent 
                                    headers={headers} 
                                    hasAction={false}
                                /> 
                            </Box>
                        :   
                            <Box sx={{mt: 3}}>
                                <ListVehicleAccessLogComponent 
                                    headers={headers} 
                                    data={vehicleAccessLogsData} 
                                />

                                {vehicleAccessLogsData?.length > 0 &&
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handleChangePagination}
                                        sx={{
                                            margin: "auto",
                                            width: "fit-content",
                                            paddingTop: 3,

                                            "& .MuiPaginationItem-root.Mui-selected": {
                                                backgroundColor: "#0b99da",      
                                                color: "#fff",               
                                            }
                                        }}
                                    />
                                }   
                            </Box>
                        }
                    </Box>
                </Stack>
            </Paper>
        </>
    )
}

export default ViewVehicleComponent